import React, { useState, useEffect } from "react";
import { User, Department } from ".clients/../types/index";
import { useData } from "../context/DataContext";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { DepartmentSelector } from "./DepartmentSelector";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { getDepartmentCode } from "../utils/helpers";
import { Can } from "../components/Can";
import { ACTIONS, RESOURCES } from "../constants/permissions.constants";

interface PersonDepartmentsProps {
  user: User;
  loggedInUser: User | null;
  readOnly?: boolean;
}

export function PersonDepartments({ user, loggedInUser, readOnly = false }: PersonDepartmentsProps) {
  const { departments, assignUserDepartments } = useData();
  const [selectedDepartments, setSelectedDepartments] = useState<string[]>([]);
  const [primaryDepartment, setPrimaryDepartment] = useState<string>("");
  const [isEditing, setIsEditing] = useState(false);
  const [additionalDepartments, setAdditionalDepartments] = useState<Department[]>([]);
  const [currentUser, setCurrentUser] = useState<User>(user);

  useEffect(() => {
    setCurrentUser(user);
  }, [user]);

  // Initialize state from user data
  useEffect(() => {
    const currentDepartments = user.departments || [];
    const departmentCode = getDepartmentCode(user.department);
    // Ensure primary department is always in the selected departments
    if (!currentDepartments.includes(departmentCode)) {
      currentDepartments.push(departmentCode);
    }
    setPrimaryDepartment(departmentCode);
    setSelectedDepartments(currentDepartments);
    setAdditionalDepartments(getAdditionalDepartments(currentUser));
  }, [currentUser, departments]);
  
  const canEdit = loggedInUser?.userRole === "Admin" || loggedInUser?.userRole === "Director"
  
  const handleSave = async () => {
    if (selectedDepartments.length === 0) {
      toast.error("Please select at least one department");
      return;
    }

    if (!primaryDepartment || !selectedDepartments.includes(primaryDepartment)) {
      toast.error("Please select a primary department");
      return;
    }

    // Save the departments
    const updatedUser = await assignUserDepartments(currentUser.id, selectedDepartments, primaryDepartment);
    
    if (updatedUser) {
      setCurrentUser(updatedUser);
      setAdditionalDepartments(getAdditionalDepartments(updatedUser));
    }

    setIsEditing(false);
  };

  const filterAdditionalDepartments = (primaryDepartment: string, selectedDepartments: string[]) => {
    const additionalDeptIds = selectedDepartments
      .filter(dept => dept !== primaryDepartment);
    
    const departmentObjects = additionalDeptIds
      .map(deptId => departments.find(d => d.departmentCode == deptId))
      .filter((dept): dept is Department => dept !== undefined);
    
    return departmentObjects
  }
  
  const getAdditionalDepartments = (user: User): Department[] => {
    if (!user.departments) {
      return [];
    }
    
    const primaryDepartmentCode = getDepartmentCode(user.department); // Assuming getDepartmentCode returns a string or number ID
    
    const additionalDeptIds = (user.departments || [])
      .filter(dept => dept !== primaryDepartmentCode);
    
    const departmentObjects = additionalDeptIds
      .map(deptId => departments.find(d => d.departmentCode == deptId))
      .filter((dept): dept is Department => dept !== undefined); // Type guard to remove undefineds
    
    return departmentObjects;
  };

  // Allow multiple departments for all roles
  const canAssignMultipleDepartments = true;
  
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Departments</CardTitle>
        {!readOnly && !isEditing && (
          <Can I={ACTIONS.UPDATE} a={RESOURCES.PERSON_DEPARTMENTS}>
            <Button variant="outline" size="sm" onClick={() => setIsEditing(true)}>
              Edit Departments
            </Button>
          </Can>
        )}
      </CardHeader>
      <CardContent>
        {isEditing ? (
          <div className="space-y-4">
            <DepartmentSelector
              departments={departments}
              selectedDepartments={selectedDepartments}
              primaryDepartment={primaryDepartment}
              onChange={setSelectedDepartments}
              onChangePrimary={setPrimaryDepartment}
              showPrimarySelector={true}
            />
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setIsEditing(false)}>
                Cancel
              </Button>
              <Button onClick={handleSave}>
                Save
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <>
              <div>
                <h4 className="font-medium">Primary Department</h4>
                {(() => {
                  const dept = departments.find(d => d.departmentCode === getDepartmentCode(user.department));
                  return dept && (
                    <Link 
                      to={`/departments/${dept.id}`}
                      className="text-muted-foreground hover:text-primary hover:underline"
                    >
                      {dept.departmentCode} - {dept.departmentName}
                    </Link>
                  );
                })()}
              </div>
              {(user.departments && user.departments.length > 0) && (
                <div>
                  <h4 className="font-medium">Additional Departments</h4>
                  <ul className="list-disc pl-5 mt-1">
                    {
                    additionalDepartments.length > 0 ? (
                      additionalDepartments.map(dept => (
                        <li key={dept.id}>
                          <Link 
                            to={`/departments/${dept.id}`}
                            className="text-muted-foreground hover:text-primary hover:underline"
                          >
                            {dept.departmentCode} - {dept.departmentName}
                          </Link>
                        </li>
                      ))
                    ) : (
                      <span className="text-muted-foreground">None</span>
                    )}
                  </ul>
                </div>
              )}
            </>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
